import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, setToken, getToken, removeToken } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check for stored auth on mount
    const token = getToken();
    const storedUser = localStorage.getItem('upim_user');
    
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
      setIsAuthenticated(true);
      
      // Verify token is still valid by fetching profile
      authAPI.getProfile()
        .then(userData => {
          setUser(userData);
          localStorage.setItem('upim_user', JSON.stringify(userData));
        })
        .catch(() => {
          // Token invalid, clear auth
          logout();
        });
    }
    setLoading(false);
  }, []);

  // Login with email/password (FASE A: 2FA y cambio obligatorio de contraseña)
  const loginWithEmail = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      if (response.totp_required) {
        return { totpRequired: true, pendingToken: response.pending_token };
      }
      const { access_token, user: userData } = response;
      setToken(access_token);
      setUser(userData);
      setIsAuthenticated(true);
      localStorage.setItem('upim_user', JSON.stringify(userData));
      return { user: userData, mustChangePassword: !!response.must_change_password };
    } catch (error) {
      throw new Error(error.message || 'Error al iniciar sesión');
    }
  };

  // Segundo factor: completa el login con TOTP o código de respaldo
  const verifyTotpLogin = async (pendingToken, code, backupCode) => {
    const response = await authAPI.verifyTotpLogin(pendingToken, code, backupCode);
    setToken(response.access_token);
    setUser(response.user);
    setIsAuthenticated(true);
    localStorage.setItem('upim_user', JSON.stringify(response.user));
    return { user: response.user, mustChangePassword: !!response.must_change_password };
  };

  // Login with Google (placeholder - to be implemented)
  const loginWithGoogle = async () => {
    // TODO: Implement Google OAuth
    throw new Error('Google login no disponible aún');
  };

  // Register new user
  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const { access_token, user: newUser } = response;
      
      setToken(access_token);
      setUser(newUser);
      setIsAuthenticated(true);
      localStorage.setItem('upim_user', JSON.stringify(newUser));
      
      return newUser;
    } catch (error) {
      throw new Error(error.message || 'Error al registrar');
    }
  };

  // Logout
  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    removeToken();
    localStorage.removeItem('upim_user');
  };

  // Update user profile
  const updateProfile = async (updates) => {
    try {
      const updatedUser = await authAPI.updateProfile(updates);
      setUser(updatedUser);
      localStorage.setItem('upim_user', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (error) {
      throw new Error(error.message || 'Error al actualizar perfil');
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    loginWithEmail,
    verifyTotpLogin,
    loginWithGoogle,
    register,
    logout,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
